import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CONSUMABLES } from "@/hooks/useShop";
import { Backpack, X } from "lucide-react";

export function InventoryPanel({ 
  isOpen, 
  onClose, 
  inventory, 
  onUseItem,
  disabled = false 
}) {
  const [usingItem, setUsingItem] = useState(null);

  if (!isOpen) return null;

  const handleUse = (itemId) => {
    setUsingItem(itemId);
    const success = onUseItem(itemId);
    setTimeout(() => setUsingItem(null), 300);
    if (success) {
      onClose();
    }
  };

  const hasAnyItems = CONSUMABLES.some(item => (inventory[item.id] || 0) > 0);

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center p-4 pointer-events-none">
      <Card 
        className="w-full max-w-md bg-gradient-to-b from-emerald-50 to-teal-50 border-2 border-emerald-400 shadow-2xl pointer-events-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="inventory-panel"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-3 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Backpack className="w-5 h-5" />
            <h3 className="font-display font-bold">Inventory</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/20 transition-colors text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3">
          {hasAnyItems ? (
            <div className="grid grid-cols-5 gap-2">
              {CONSUMABLES.map((item) => {
                const count = inventory[item.id] || 0;
                if (count === 0) return null;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => handleUse(item.id)}
                    disabled={disabled || usingItem === item.id}
                    data-testid={`use-${item.id}`}
                    className={`relative flex flex-col items-center justify-center p-2 rounded-xl border-2 transition-all ${
                      disabled 
                        ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
                        : 'bg-white border-emerald-200 hover:border-emerald-400 hover:scale-105 active:scale-95'
                    }`}
                    title={`${item.name}: ${item.description}`}
                  >
                    <span className="text-2xl">{item.emoji}</span>
                    <Badge className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs px-1.5 min-w-[20px]">
                      {count}
                    </Badge>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-sm text-emerald-700 py-4">
              No items. Buy items in the Shop! 🛒
            </p>
          )}
        </div>

        {/* Footer hint */}
        {hasAnyItems && (
          <div className="px-3 pb-3">
            <p className="text-xs text-emerald-600 text-center">
              Tap an item to use
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

// Inventory button for game controls
export function InventoryButton({ onClick, itemCount, disabled }) {
  return (
    <Button
      onClick={onClick}
      variant="outline"
      size="default"
      disabled={disabled}
      data-testid="inventory-button"
      className="font-display font-semibold bg-gradient-to-r from-emerald-100 to-teal-100 hover:from-emerald-200 hover:to-teal-200 border-2 border-emerald-400 text-emerald-800 shadow-card relative"
    >
      <Backpack className="w-4 h-4 mr-2" />
      Items
      {itemCount > 0 && (
        <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-1.5 min-w-[20px]">
          {itemCount}
        </Badge>
      )}
    </Button>
  );
}
